import { useEffect, useState } from 'react';

import { Button, Stack, TextField } from '@mui/material';

import * as DataListAPI from '../../../api/dataList';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import SystemTabs from '../../../ui/SystemTabs';
import Checkbox from '../../form/Checkbox';
import DataList from '../../form/DataList';
import SelectEmpresa from '../../form/SelectEmpresa';

function Form({
    dados,
    setDados,
    handleSubmit,
    caminhoVoltar,
    OptionsEmpresa,
    editando,
    carregando,
    salvando,
    falhaCarregamento,
}) {
    const navigate = useNavigateParams();
    const { setFlashMessage } = useFlashMessage();
    const [optionsPessoa, setOptionsPessoa] = useState([]);
    const [pessoa, setPessoa] = useState(dados?.Pessoa || {});

    useEffect(() => {
        setPessoa(dados?.Pessoa || {});
    }, [dados?.Pessoa]);

    async function consultarPessoas(filtro) {
        if (!dados?.empresaId) {
            setOptionsPessoa([]);
            return [];
        }

        try {
            const result = await DataListAPI.dataList({
                empresaId: dados.empresaId,
                tabela: 'Pessoa',
                filtro,
            });

            setOptionsPessoa(result);
            return result;
        } catch (error) {
            setOptionsPessoa([]);
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar as pessoas.',
                error.response?.data?.type || 'error'
            );
            return [];
        }
    }

    async function handleChange(event) {
        const { name, value, type, checked } = event.target;

        if (name === 'empresaId') {
            setPessoa({});
            setOptionsPessoa([]);
            setDados((anterior) => ({
                ...anterior,
                empresaId: value,
                pessoaId: null,
                Pessoa: {},
            }));
            return;
        }

        if (name === 'Pessoa.descricao') {
            setPessoa((anterior) => ({
                ...anterior,
                descricao: value,
            }));

            const opcoes = await consultarPessoas(value);
            const opcaoSelecionada = opcoes.find((opcao) => String(opcao?.descricao || opcao?.label || '') === String(value));
            const selectedId = opcaoSelecionada?.id ?? document.querySelector(`datalist option[value="${value}"]`)?.dataset.id;

            setDados((anterior) => ({
                ...anterior,
                pessoaId: selectedId ? Number(selectedId) : null,
                Pessoa: {
                    ...anterior.Pessoa,
                    descricao: value,
                },
            }));
            return;
        }

        if (name === 'dataFim' && !value) {
            setDados((anterior) => ({
                ...anterior,
                dataFim: '',
                motivoDesligamento: '',
            }));
            return;
        }

        setDados((anterior) => ({
            ...anterior,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }

    function submit(event) {
        event.preventDefault();
        handleSubmit(dados);
    }

    const tabVinculo = (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <SelectEmpresa
                    name="empresaId"
                    text="Empresa"
                    options={OptionsEmpresa}
                    handleOnChange={handleChange}
                    value={dados?.empresaId || ''}
                    disabled={editando}
                    required
                    notOptNull={false}
                />

                <DataList
                    text="Pessoa associada"
                    name="Pessoa.descricao"
                    inputWidth="600px"
                    filteredOptions={optionsPessoa}
                    handleOnChange={handleChange}
                    value={pessoa?.descricao || ''}
                    readonly={editando || !dados?.empresaId}
                    required
                />
            </Stack>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <TextField
                    name="dataInicio"
                    label="Início da associação"
                    type="date"
                    value={dados?.dataInicio || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                />

                <TextField
                    name="dataFim"
                    label="Término da associação"
                    type="date"
                    value={dados?.dataFim || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                />
            </Stack>

            <TextField
                name="motivoDesligamento"
                label="Motivo do desligamento"
                value={dados?.motivoDesligamento || ''}
                onChange={handleChange}
                disabled={!dados?.dataFim}
                fullWidth
            />

            <Checkbox
                title="Indica que o registro pode ser utilizado nas rotinas da associação"
                text="Ativo"
                name="ativo"
                handleOnChange={handleChange}
                value={dados?.ativo}
                checked={Boolean(dados?.ativo)}
            />

            <TextField
                name="observacao"
                label="Observação"
                value={dados?.observacao || ''}
                onChange={handleChange}
                multiline
                minRows={3}
                fullWidth
            />
        </Stack>
    );

    const tabsConfig = [
        {
            key: 'vinculo',
            label: 'Vínculo',
            title: 'Histórico associativo da pessoa',
            panel: tabVinculo,
        },
    ];

    return (
        <form onSubmit={submit} autoComplete="off">
            <h1>{editando ? 'EDIÇÃO DO VÍNCULO DO ASSOCIADO' : 'CADASTRO DO VÍNCULO DO ASSOCIADO'}</h1>

            <SystemTabs tabs={tabsConfig} initialKey="vinculo" />

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                    variant="contained"
                    color="secondary"
                    type="button"
                    disabled={salvando}
                    onClick={() => navigate(caminhoVoltar)}
                >
                    Voltar
                </Button>

                <Button type="submit" variant="contained" color="success" disabled={carregando || salvando || falhaCarregamento}>
                    {salvando ? 'Salvando...' : editando ? 'Atualizar' : 'Cadastrar'}
                </Button>
            </Stack>
        </form>
    );
}

export default Form;
